import type { ParsedUrlQuery } from 'querystring';
import type { LocationState } from 'history';
// eslint-disable-next-line custom/no-restricted-imports
import {
  keys,
  intersection,
  contains,
  isEmpty,
  symmetricDifferenceWith,
  eqBy,
} from 'ramda';
import type { AppliedFilter, FilterName, SortValue } from '@engage/filters/models';
import type { AvailableState } from '@engage/filters/redux';
import { DEFAULT_SORT_STATE, filtersReducer } from '@engage/filters/redux';
import { getAllAvailableFilters, getAvalilableSorts } from '@engage/filters/selectors';

export default filtersReducer;

const safelyParseQueryParam = (queryParams: Record<string, string>, name: string) => {
  try {
    return JSON.parse(queryParams[name]);
  } catch (e) {
    return [];
  }
};

// Selectors
export const getCurrentFiltersFromSearch = (
  state: SelectorState,
  query: ParsedUrlQuery,
): AppliedFilter[] => {
  const queryParams = supportIsFreemiumParam(query);
  const availableFilterNames = getAllAvailableFilters(state).map(n => n.name);
  const filterNamesInSearch = intersection(keys(queryParams), availableFilterNames);

  return filterNamesInSearch.reduce((acc, filterName) => {
    const parsedValue = safelyParseQueryParam(queryParams, filterName);
    // Prevent malformed URL query params from crashing the UI
    if (Array.isArray(parsedValue) && parsedValue.length > 0) {
      acc.push({
        name: filterName as FilterName,
        value: parsedValue,
      });
    }
    return acc;
  }, [] as AppliedFilter[]);
};

// maps is_freemium query param to app-free to support both deeplinks
const supportIsFreemiumParam = (queryParams: ParsedUrlQuery) => {
  const queryParam = {};
  for (const key in queryParams) {
    if (key === 'is_freemium') {
      if (queryParams[key] === 'true') {
        // @ts-expect-error (TS7053): Element implicitly has an 'any' type because expression of type '"app-free"' can't be used to index type '{}'.
        queryParam['app-free'] = '["true"]';
      } else {
        // @ts-expect-error (TS7053): Element implicitly has an 'any' type because expression of type '"app-free"' can't be used to index type '{}'.
        queryParam['app-free'] = queryParams[key];
      }
    } else {
      // @ts-expect-error (TS7053): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
      queryParam[key] = queryParams[key];
    }
  }

  return queryParam;
};

export const getCurrentSortFromSearch = (
  state: SelectorState,
  query: ParsedUrlQuery,
): SortValue => {
  const sort = (query.sort as string) ?? '';
  const desc = (query.desc as string) ?? false;
  const availableSorts = getAvalilableSorts(state);
  const availableSortValues = availableSorts.map(n => n.value.sort);

  if (!contains(sort, availableSortValues)) {
    return DEFAULT_SORT_STATE;
  }

  return { sort, desc: toBoolValue(desc) };
};

const toBoolValue = (bool: string) => bool.toLowerCase() === 'true';

type SelectorState = LocationState & {
  filters: {
    available: AvailableState;
  };
};

const filterToString = (filter: AppliedFilter) => `${filter.name}${filter.value}`;
const eqByString = eqBy(filterToString);

export const filtersEqual = (a: AppliedFilter[], b: AppliedFilter[]) => {
  const diff = symmetricDifferenceWith(eqByString, a, b);
  return isEmpty(diff);
};
